import LoginView from '@/modules/auth/pages/Auth-Login/LoginView.vue'
import RegisterView from '@/modules/auth/pages/Register/RegisterView.vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { isAuthenticatedGuard } from './auth-guard'

// Función para obtener la primera ruta disponible según el rol del usuario
export function getFirstAvailableRoute(userRole: string | null): string {
  if (!userRole) return '/login'

  const authenticatedRoute = routes.find(r => r.meta?.requiresAuth && r.children)
  if (!authenticatedRoute?.children) return '/login'

  const availableRoutes = authenticatedRoute.children
    .filter(route => {
      const roles = route.meta?.roles as string[] | undefined
      const showInMenu = route.meta?.showInMenu
      return showInMenu && roles?.includes(userRole)
    })
    .sort((a, b) => {
      const orderA = (a.meta?.order as number) || 999
      const orderB = (b.meta?.order as number) || 999
      return orderA - orderB
    })

  const firstRoute = availableRoutes[0]
  if (firstRoute) {
    return `/${firstRoute.path}`
  }

  return '/login'
}

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'Home', redirect: '/login' },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      guestOnly: true,
      title: 'Iniciar Sesión',
    },
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: {
      guestOnly: true,
      title: 'Registro de Apoderado',
    },
  },
  {
    path: '/',
    redirect: '/inicio',
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'inicio',
        name: 'ApoderadoInicio',
        component: () => import('@/modules/apoderado/pages/Inicio/InicioView.vue'),
        meta: {
          title: 'Inicio',
          icon: 'mdi-home',
          showInMenu: true,
          order: 1,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'estudiantes',
        name: 'ApoderadoEstudiantes',
        component: () => import('@/modules/apoderado/pages/Estudiantes/MisEstudiantesView.vue'),
        meta: {
          title: 'Estudiantes',
          icon: 'mdi-account-school',
          showInMenu: true,
          order: 2,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'matricular',
        name: 'ApoderadoMatricular',
        component: () => import('@/modules/apoderado/pages/Matricular/MatricularView.vue'),
        meta: {
          title: 'Matricular',
          icon: 'mdi-school',
          showInMenu: true,
          order: 3,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'matriculas',
        name: 'ApoderadoMatriculas',
        component: () => import('@/modules/apoderado/pages/Matriculas/MatriculasView.vue'),
        meta: {
          title: 'Mis Matrículas',
          icon: 'mdi-file-document-multiple',
          showInMenu: true,
          order: 4,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'horarios',
        name: 'ApoderadoHorarios',
        component: () => import('@/modules/apoderado/pages/Horario/HorarioView.vue'),
        meta: {
          title: 'Horarios',
          icon: 'mdi-calendar-clock',
          showInMenu: true,
          order: 5,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'matriculas/:id',
        name: 'ApoderadoDetalleMatricula',
        component: () =>
          import('@/modules/apoderado/pages/DetalleMatricula/DetalleMatriculaView.vue'),
        meta: {
          title: 'Detalle de Matrícula',
          showInMenu: false,
          roles: ['GUARDIAN'],
        },
      },
      {
        path: 'attendance',
        name: 'MySections',
        component: () => import('@/modules/attendance/pages/MySections/MySectionsView.vue'),
        meta: {
          title: 'Asistencia',
          icon: 'mdi-clipboard-check-outline',
          showInMenu: true,
          order: 6,
          roles: ['ASSISTANT'],
        },
      },
      {
        path: 'attendance/:sectionId',
        name: 'TakeAttendance',
        component: () => import('@/modules/attendance/pages/TakeAttendance/TakeAttendanceView.vue'),
        meta: {
          title: 'Tomar Asistencia',
          showInMenu: false,
          roles: ['ADMIN', 'TEACHER', 'ASSISTANT'],
        },
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/modules/dashboard/pages/DashboardView/DashboardView.vue'),
        meta: {
          title: 'Dashboard',
          icon: 'mdi-view-dashboard',
          showInMenu: true,
          order: 8,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'enrollments',
        name: 'ListEnrollments',
        component: () => import('@/modules/enrollment/pages/ListEnrollments/ListEnrollment.vue'),
        meta: {
          title: 'Gestión de Matrículas',
          icon: 'mdi-clipboard-text-search',
          showInMenu: true,
          order: 9,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'sections',
        name: 'ListSections',
        component: () => import('@/modules/sections/pages/ListSections/ListSection.vue'),
        meta: {
          title: 'Secciones',
          icon: 'mdi-shape-outline',
          showInMenu: true,
          order: 10,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'courses',
        name: 'ListCourses',
        component: () => import('@/modules/courses/pages/ListCourses/ListCourse.vue'),
        meta: {
          title: 'Cursos',
          icon: 'mdi-handball',
          showInMenu: true,
          order: 11,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'periods',
        name: 'ListPeriods',
        component: () => import('@/modules/periods/pages/ListPeriods/ListPeriod.vue'),
        meta: {
          title: 'Periodos',
          icon: 'mdi-calendar-range',
          showInMenu: true,
          order: 12,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'users',
        name: 'ListUsers',
        component: () => import('@/modules/users/pages/ListUsers/ListUser.vue'),
        meta: {
          title: 'Usuarios',
          icon: 'mdi-account-group',
          showInMenu: true,
          order: 13,
          roles: ['ADMIN'],
        },
      },
      {
        path: 'public-types',
        name: 'ListPublicTypes',
        component: () => import('@/modules/public-types/pages/ListPublicTypes/ListPublicType.vue'),
        meta: {
          title: 'Tipos de Público',
          icon: 'mdi-account-multiple-outline',
          showInMenu: true,
          order: 14,
          roles: ['ADMIN'],
        },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

router.afterEach((to) => {
  const defaultTitle = 'Sistema de Vacaciones Útiles - UNMSM'
  document.title = (to.meta.title as string) || defaultTitle
})

router.beforeEach(isAuthenticatedGuard)

export default router
